import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Palette, Search, LayoutGrid, LineChart, PenTool, LifeBuoy, RefreshCw, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Container, Reveal, SectionLabel } from "../components/primitives";
import Seo from "../components/Seo";

const services = [
  { icon: Palette, title: "Custom Web Design", desc: "Premium, on-brand designs crafted to make your business look established and credible." },
  { icon: LayoutGrid, title: "Responsive Development", desc: "Pixel-perfect websites that work beautifully across desktop, tablet and mobile." },
  { icon: Search, title: "Local SEO", desc: "Optimized titles, meta, schema and sitemaps so local customers find you first." },
  { icon: LineChart, title: "Lead Generation", desc: "Conversion-focused forms, quote requests and CTAs that turn visitors into leads." },
  { icon: PenTool, title: "Content & Copy", desc: "AI-assisted, polished copy that speaks to your customers and drives action." },
  { icon: LifeBuoy, title: "Website Care", desc: "Hosting, security, backups and updates so your site stays fast and safe." },
];

export default function Services() {
  const navigate = useNavigate();
  return (
    <div data-testid="services-page" className="pt-32">
      <Seo page="services" />
      <Container>
        <Reveal>
          <SectionLabel>Services</SectionLabel>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tighter text-[#F3EDDC] max-w-3xl">
            Full-service web design for local businesses
          </h1>
          <p className="mt-5 text-lg text-[#B7AF98] max-w-2xl">
            Everything you need to launch, grow and maintain a website that actually brings in customers.
          </p>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={i * 60}>
              <div className="rounded-2xl border border-white/10 bg-[#141414] p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-transform duration-300 h-full">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#C9A227]/10 text-[#C9A227]">
                  <s.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-[#F3EDDC]">{s.title}</h3>
                <p className="mt-2 text-[#B7AF98] leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Featured: Website Refresh */}
        <Reveal>
          <div data-testid="services-refresh" className="mt-8 rounded-3xl border border-[#C9A227]/30 bg-[#141414] p-8 sm:p-12 grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#C9A227]/10 text-[#C9A227] px-3 py-1 text-xs font-medium">
                <RefreshCw className="h-3.5 w-3.5" /> Already have a website?
              </div>
              <h2 className="mt-4 font-display text-3xl font-bold text-[#F3EDDC]">Website Refresh</h2>
              <p className="mt-3 text-[#B7AF98] leading-relaxed max-w-xl">
                Don't start from scratch. We redesign and update your existing website with a modern look, mobile optimization, refreshed content and an SEO tune-up — so it works harder for your business.
              </p>
              <div className="mt-5 grid sm:grid-cols-2 gap-2 max-w-lg">
                {["Full redesign", "Mobile optimization", "Content updates", "SEO refresh", "2 revisions"].map((f) => (
                  <span key={f} className="flex items-center gap-2 text-sm text-[#D2CAB2]"><Check className="h-4 w-4 text-[#C9A227]" /> {f}</span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2 lg:text-right">
              <div className="font-display text-5xl font-bold gold-text inline-block">$697</div>
              <div className="text-sm text-[#7E7663] mt-1">one-time</div>
              <div className="mt-5">
                <Button data-testid="services-refresh-cta" onClick={() => navigate("/pricing")} className="gold-shimmer-btn rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12 px-8">
                  Refresh my website <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="my-24 rounded-3xl bg-black text-white p-10 sm:p-14 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Not sure which service you need?</h2>
            <p className="mt-2 text-[#7E7663]">Tell us about your business and we'll recommend the right plan.</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate("/contact")} className="rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12 px-7">Get a free quote</Button>
            <Button onClick={() => navigate("/pricing")} variant="outline" className="rounded-full h-12 px-7 border-white/30 bg-transparent text-white hover:bg-[#141414]/10">See pricing <ArrowRight className="ml-1 h-4 w-4" /></Button>
          </div>
        </div>
      </Container>
    </div>
  );
}
